import React from 'react';
import otherwise from 'lodash/stubTrue';
import cond from 'lodash/cond';
import { fg } from '@atlassian/jira-feature-gating';
import {
	isMobileAndInMVPExperiment,
	isMobileAndInViewPanelIterationExperiment,
} from '@atlassian/jira-mobile-web/src/index.tsx';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils';
import type { SoftwareWorkSuggestionsYourWorkEntryPoint } from '@atlassian/jira-software-work-suggestions-your-work/entrypoint.tsx';
import type { EntryPointReferenceSubject } from '@atlassian/jira-entry-point/src/controllers/utils/use-entry-point-load-manager/index.tsx';
import type { Services } from '../common/types/services.tsx';
import AssignedToUserProvider from '../services/assigned-to-user-provider/index.tsx';
import { BoardsProvider } from '../services/boards-provider/index.tsx';
import FavouritesProvider from '../services/favourites-provider/index.tsx';
import RecentActivityWorkedOnProvider from '../services/recent-activity-provider/index.tsx';
import RecentlyViewedProvider from '../services/recently-viewed-provider/index.tsx';
import { WORK_SUGGESTIONS_TAB_KEY } from '../common/constants/tab-content.tsx';
import type { TabConfig } from '../common/types/tabs.tsx';
import { WorkSuggestionsProvider } from '../services/work-suggestions-provider/index.tsx';
import { MOBILE_WEB_TABS, MOBILE_WEB_WITH_VIEWED_TABS, NAV_V4_TABS } from './constants.tsx';
import { WorkSuggestionsTab } from './tabs/work-suggestions/index.tsx';

export type InjectedServices = {
	services: Services;
};

export const services: Services = {
	WorkedOn: RecentActivityWorkedOnProvider,
	RecentlyViewed: RecentlyViewedProvider,
	AssignedToUser: AssignedToUserProvider,
	Favourites: FavouritesProvider,
	WorkSuggestions: WorkSuggestionsProvider,
};

export const servicesNav4: Services = {
	...services,
	Boards: BoardsProvider,
};

export const getWorkSuggestionsTab = (
	entryPointSubject: EntryPointReferenceSubject<typeof SoftwareWorkSuggestionsYourWorkEntryPoint>,
	onClickCallback: () => void,
): TabConfig => ({
	id: WORK_SUGGESTIONS_TAB_KEY,
	provider: 'WorkSuggestions',
	showCount: false,
	tabContent: () => <WorkSuggestionsTab entryPointReferenceSubject={entryPointSubject} />,
	onClickCallback,
});

const getTabConfigToUseWithMobileViewTab = cond([
	[() => isMobileAndInViewPanelIterationExperiment(), () => MOBILE_WEB_WITH_VIEWED_TABS],
	[() => isMobileAndInMVPExperiment(), () => MOBILE_WEB_TABS],
	[otherwise, () => NAV_V4_TABS],
]);

const getTabConfigToUseWithoutMobileViewTab = () => {
	const isMobileWebAndInMVPExperiment = isMobileAndInMVPExperiment();
	const tabConfigForNav = NAV_V4_TABS;
	return isMobileWebAndInMVPExperiment ? MOBILE_WEB_TABS : tabConfigForNav;
};

export const getTabConfigToUse = functionWithCondition(
	() => fg('jira_mobile_web_mvp_view_panel_kill_switch'),
	getTabConfigToUseWithMobileViewTab,
	getTabConfigToUseWithoutMobileViewTab,
);
